import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * Employee expense claims (ERD §5.10; FRD-FIN-080, FIN-082 to FIN-090; CLR-11). Money is a decimal
 * string at the amount scale (SDD §9.2). Posting a claim as payable and paying it need the employee
 * claim payable account, which the approved COA does not contain (CLR-02).
 */
const AmountSchema = z
    .string()
    .regex(/^\d{1,17}(\.\d{1,2})?$/, 'must be a decimal amount with at most 2 decimals');
const BusinessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'must be YYYY-MM');
const SummarySchema = z.object({ id: IdSchema, summary: z.string().nullable() });
export const ExpenseCategorySchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    accountId: IdSchema,
    accountCode: z.string().nullable(),
    accountName: z.string().nullable(),
    /** The dimensions the category's account requires of a claim line (FRD-FIN-084). */
    requires: z.object({ costCenter: z.boolean(), project: z.boolean(), client: z.boolean() }),
    status: z.string(),
    version: z.number().int(),
});
export const ExpenseCategoryListSchema = z.array(ExpenseCategorySchema);
export const CreateExpenseCategoryRequestSchema = z.strictObject({
    code: z.string().regex(/^[A-Z][A-Z0-9_-]{0,19}$/, 'upper-case letters, digits, - or _'),
    name: z.string().trim().min(1).max(100),
    accountId: IdSchema,
});
export const UpdateExpenseCategoryRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(100),
    accountId: IdSchema,
    status: z.enum(['active', 'inactive']),
});
export const ExpenseClaimLineRequestSchema = z.strictObject({
    expenseDate: BusinessDateSchema,
    description: z.string().trim().min(1).max(500),
    amount: AmountSchema,
    expenseCategoryId: IdSchema,
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    clientId: IdSchema.optional(),
    locationId: IdSchema.optional(),
});
/** A claim for one month (FRD-FIN-085); without `employeeId` it is the caller's own. */
export const ExpenseClaimRequestSchema = z.strictObject({
    employeeId: IdSchema.optional(),
    claimMonth: MonthSchema,
    description: z.string().trim().max(500).optional(),
    lines: z.array(ExpenseClaimLineRequestSchema).min(1).max(200),
});
export const ExpenseClaimSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    employeeId: IdSchema,
    employeeLabel: z.string().nullable(),
    departmentId: IdSchema.nullable(),
    claimMonth: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    totalAmount: z.string(),
    payableTotal: z.string(),
    paidAmount: z.string(),
    outstandingAmount: z.string(),
    journalId: IdSchema.nullable(),
    submittedAt: z.string().nullable(),
    approvedAt: z.string().nullable(),
    version: z.number().int(),
});
export const ExpenseClaimLineSchema = z.object({
    id: IdSchema,
    lineNo: z.number().int(),
    expenseDate: z.string(),
    description: z.string(),
    amount: z.string(),
    expenseCategoryId: IdSchema,
    categoryName: z.string().nullable(),
    accountCode: z.string().nullable(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
});
export const ExpenseClaimDetailSchema = z.object({
    claim: ExpenseClaimSchema,
    lines: z.array(ExpenseClaimLineSchema),
    journal: SummarySchema.nullable(),
    payments: z.array(z.object({
        paymentId: IdSchema,
        documentNo: z.string(),
        paymentDate: z.string(),
        allocatedAmount: z.string(),
        status: z.string(),
    })),
});
/** Finance posts an approved claim; the posting date defaults to today (FRD-FIN-088). */
export const PostExpenseClaimRequestSchema = z.strictObject({
    postingDate: BusinessDateSchema.optional(),
});
/** A payment to an employee settling posted claims (FRD-FIN-080, FIN-088; MD-09). */
export const EmployeePaymentRequestSchema = z.strictObject({
    employeeId: IdSchema,
    paymentDate: BusinessDateSchema,
    bankCashAccountId: IdSchema,
    reference: z.string().trim().max(100).optional(),
    description: z.string().trim().max(500).optional(),
    claims: z
        .array(z.strictObject({ expenseClaimId: IdSchema, allocatedAmount: AmountSchema }))
        .min(1)
        .max(100),
});
//# sourceMappingURL=expenses.js.map